import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Card, CardBody, Col } from 'reactstrap';
import moment from 'moment';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TablePagination from '@material-ui/core/TablePagination';
import Checkbox from '@material-ui/core/Checkbox';
import { Tooltip } from '@material-ui/core';

import UnitsSearchAppBar from '../../../shared/components/building_filters/UnitsSearchAppBar';
import LinearLoading from '../../../shared/components/LinearLoading';
import BuildingIcons from '../../../shared/components/BuildingIcons';
import AddCollectionButton from '../../../shared/components/collections/AddCollectionButton';
import ToggleChips from '../../OwnerOutreach/components/ToggleChips';
import ForeclosureDetailModal from './ForeclosureDetailModal';
import SkipTraceUnifiedModal from '../../../shared/components/skiptrace/SkipTraceUnifiedModal';

import { _axiosCall } from '../../../shared/helpers/apicalls';
import {
	_capitalizeText,
	_formatNumber,
	_nameToUrl,
	_getBuildingURL,
	_replaceAbbreviations,
	_isEqual,
	_tranformTagsQuery,
	_getBuildingPhotos,
	_getFirstAndLastFromFullName,
	_extractIndividualNames,
	propertyFL,
	syncURLFilters,
	syncURLSearch,
	setURLFromState,
	setLocalStorage,
	_isHouse,
} from '../../../shared/helpers/utils';
import { _foreclosureSearchFilters } from '../../../shared/helpers/filters';

const buildingUrl = process.env.API_ROOT + '/query/';
const lsPre = 'FS_';
const subFilterKey = 'subFilters';
const defaultSubFilter = {};

const NYSCEF_BASE_FQ = '{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}*:*';

const ACRIS_DISTRESS_DOC_TYPES = ['fl', 'ftl', 'tls', 'ntxl', 'dpftl', 'judg', 'locc', 'tolcc', 'brup'];

const FORECLOSURE_TYPE_CHIPS = [
	{ key: 1, label: 'Lis Pendens',             filter: { distress_source: ['lis_pendens'] },     multiSelect: true },
	{ key: 2, label: 'Upcoming Auctions',       filter: { auction_date_bucket: ['upcoming_90'] }, multiSelect: true },
	{ key: 3, label: 'Sold at Auction',         filter: { distress_source: ['sold_at_auction'] },  multiSelect: true },
	{ key: 4, label: 'Distress Liens',          filter: { distress_source: ['acris_liens'] },      multiSelect: true },
];

const CASE_STAGE_LABELS = {
	filed: 'Filed',
	lis_pendens_filed: 'Lis Pendens',
	summons_complaint: 'Summons & Complaint',
	rji_filed: 'RJI Filed',
	motion_for_default: 'Motion for Default',
	referee_appointed: 'Referee Appointed',
	judgment_of_foreclosure: 'Judgment',
	auction_scheduled: 'Auction Scheduled',
};

// Soft-tinted badge palette matching the Owner Outreach Lis Pendens badge
// (rgba bg @ 13%, dark text, rgba border @ 45%).
const PALETTES = {
	red:    { bg: 'rgba(220,53,69,0.13)',  text: '#a01228', border: 'rgba(220,53,69,0.45)' },
	orange: { bg: 'rgba(224,123,29,0.13)', text: '#7a3a02', border: 'rgba(224,123,29,0.45)' },
	green:  { bg: 'rgba(35,178,109,0.13)', text: '#0e6638', border: 'rgba(35,178,109,0.45)' },
	purple: { bg: 'rgba(120,15,158,0.13)', text: '#5a0875', border: 'rgba(120,15,158,0.45)' },
	blue:   { bg: 'rgba(63,127,225,0.13)', text: '#1f4fa3', border: 'rgba(63,127,225,0.45)' },
	grey:   { bg: 'rgba(133,138,161,0.13)', text: '#5C6273', border: 'rgba(133,138,161,0.45)' },
};

const CASE_STAGE_PALETTE = {
	filed: PALETTES.grey,
	lis_pendens_filed: PALETTES.purple,
	summons_complaint: PALETTES.blue,
	rji_filed: PALETTES.blue,
	motion_for_default: PALETTES.orange,
	referee_appointed: PALETTES.orange,
	judgment_of_foreclosure: PALETTES.red,
	auction_scheduled: PALETTES.red,
};

const AUCTION_STATUS_COLORS = {
	scheduled: '#D43F3F',
	postponed: '#E07B1D',
	sold: '#23B26D',
	unknown: '#A3ADC2',
};

const badgeStyle = (p) => ({
	display: 'inline-block',
	padding: '1px 8px',
	borderRadius: 15,
	fontSize: 11,
	fontWeight: 500,
	background: p.bg,
	color: p.text,
	border: '1px solid ' + p.border,
	whiteSpace: 'nowrap',
});

const ACRIS_DOC_LABELS = {
	fl: 'Foreclosure Lien',
	ftl: 'Federal Tax Lien',
	tls: 'Tax Lien',
	ntxl: 'Non-Tax Lien',
	dpftl: 'Property Tax Lien',
	judg: 'Judgment',
	locc: 'Common Lien',
	tolcc: 'Mechanics Lien',
	brup: 'Bankruptcy',
};

const HEADERS = [
	{ id: 'address', label: 'Address', sort: false },
	{ id: 'distress_signals', label: 'Distress Signals', sort: false },
	{ id: 'owner', label: 'Owner', sort: true },
	{ id: 'ownership_type', label: 'Type', sort: true },
	{ id: 'lis_pendens_date', label: 'Lis Pendens Date', sort: true },
	{ id: 'case_filing_date', label: 'Filing Date', sort: true },
	{ id: 'original_amount', label: 'Original Amount', sort: false },
	{ id: 'case_stage', label: 'Case Stage', sort: false },
	{ id: 'auction_sale_date', label: 'Auction Date', sort: false },
	{ id: 'auction_upset_price', label: 'Upset Price', sort: false },
	{ id: 'last_sold_price', label: 'Last Sold', sort: true },
];

// Sort keys that live on the case index — these flip the query to case-primary.
const CASE_SORT_FIELDS = {
	case_filing_date: 'filing_date',
	lis_pendens_date: 'lis_pendens_date',
};

const ROW_PRIORITY = {
	auction_scheduled: 100,
	judgment_of_foreclosure: 80,
	referee_appointed: 70,
	motion_for_default: 60,
	rji_filed: 50,
	summons_complaint: 40,
	lis_pendens_filed: 30,
	filed: 20,
};

function pickPrimaryCase(cases) {
	if (!Array.isArray(cases) || cases.length === 0) return null;
	const sorted = [...cases].sort((a, b) => {
		const ap = ROW_PRIORITY[a.case_stage] || 0;
		const bp = ROW_PRIORITY[b.case_stage] || 0;
		if (ap !== bp) return bp - ap;
		const ad = a.filing_date ? new Date(a.filing_date).getTime() : 0;
		const bd = b.filing_date ? new Date(b.filing_date).getTime() : 0;
		return bd - ad;
	});
	return sorted[0];
}

function _formatDate(val) {
	if (!val) return '--';
	const m = moment(val);
	if (!m.isValid() || m.year() < 1971) return '--';
	return m.format('M/D/YYYY');
}

class ForeclosureSearchTable extends PureComponent {
	state = {
		order: 'desc',
		orderBy: 'lis_pendens_date',
		page: 0,
		rowsPerPage: 50,
		start: 0,
		fetchInProgress: false,
		tableData: false,
		totalCount: 0,
		searchText: syncURLSearch(this.props, 'searchText'),
		subFilters: syncURLFilters(this.props, _foreclosureSearchFilters, subFilterKey, lsPre, defaultSubFilter),
		casesByProperty: {},
		liensByProperty: {},
		selectedUnits: {},
		modalOpen: false,
		modalCase: null,
		modalProperty: null,
		modalLiens: [],
		openModalKey: null,
	};

	_propertyFilter = _foreclosureSearchFilters;
	_requestId = 0;

	componentDidMount() {
		this._getProperties();
	}

	componentDidUpdate(prevProps, prevState) {
		const filtersChanged = !_isEqual(prevState.subFilters, this.state.subFilters);
		const searchChanged = prevState.searchText !== this.state.searchText;
		const sortChanged = prevState.order !== this.state.order || prevState.orderBy !== this.state.orderBy;
		const pageChanged = prevState.page !== this.state.page || prevState.rowsPerPage !== this.state.rowsPerPage;
		const tagsChanged = !_isEqual(prevProps.searchTags, this.props.searchTags);
		if (filtersChanged || searchChanged || sortChanged || pageChanged || tagsChanged) {
			const resetPage = !pageChanged;
			this._getProperties(resetPage);
		}
	}

	_buildQuery() {
		const fq = ['deleted:false'];
		let q = '*:*';

		const { subFilters, searchText } = this.state;
		const distressSelected = Array.isArray(subFilters.distress_source) && subFilters.distress_source.length > 0;
		if (!distressSelected) {
			fq.push(NYSCEF_BASE_FQ);
		}
		if (subFilters && Object.keys(subFilters).length > 0) {
			Object.keys(subFilters).forEach(key => {
				const val = subFilters[key];
				if (!val || val === '') return;
				const filterDef = this._propertyFilter.find(f => f.search_key === key);
				if (!filterDef || !filterDef.multi_filter) return;
				const selected = Array.isArray(val) ? val : [val];
				const matched = filterDef.values.filter(v => selected.indexOf(v.filter) > -1);
				const qVals = matched.filter(v => v.search_value).map(v => v.search_value);
				const fqVals = matched.filter(v => v.additional_fq).map(v => v.additional_fq);
				if (qVals.length > 0) {
					if (filterDef.target === 'fq') {
						fq.push(`${key}:(${qVals.join(' OR ')})`);
					} else {
						q += ` AND (${key}:(${qVals.join(' OR ')}))`;
					}
				}
				fqVals.forEach(f => fq.push(f));
			});
		}

		if (searchText && searchText.trim()) {
			const term = searchText.trim();
			const tokens = term.split(/\s+/);
			const tokenQ = tokens
				.map(t => `(alternate_addresses:(*${t.toLowerCase()}*) OR owner:(*${t.toLowerCase()}* *${t.toUpperCase()}*))`)
				.join(' AND ');
			q += ` AND (${tokenQ})`;
		}

		const { searchTags } = this.props;
		if (Array.isArray(searchTags) && searchTags.length > 0) {
			const tagQ = _tranformTagsQuery(searchTags, 'building');
			if (tagQ) {
				q += ` AND (${tagQ})`;
			}
		}

		return { q, fq };
	}

	_getProperties = async (resetPage) => {
		const requestId = ++this._requestId;
		this.setState({ fetchInProgress: true });
		await new Promise(r => setTimeout(r, 0));
		if (requestId !== this._requestId) return;

		if (CASE_SORT_FIELDS[this.state.orderBy]) {
			return this._getPropertiesCasePrimary(resetPage, requestId);
		}
		return this._getPropertiesPropertyPrimary(resetPage, requestId);
	};

	_getPropertiesPropertyPrimary = async (resetPage, requestId) => {
		const { q, fq } = this._buildQuery();
		const sort = this.state.orderBy ? `${this.state.orderBy} ${this.state.order}` : 'last_sold_date desc';
		const start = resetPage ? 0 : this.state.start;

		const axiosData = {
			url: buildingUrl + 'we3-properties?cache=30',
			method: 'post',
			query: {
				q,
				fq,
				sort,
				fl: propertyFL,
				rows: this.state.rowsPerPage,
				start,
				wt: 'json',
			},
		};

		try {
			const resp = await _axiosCall(axiosData);
			if (requestId !== this._requestId) return;
			if (!resp || !resp.docs) {
				this.setState({ fetchInProgress: false, tableData: [], totalCount: 0 });
				return;
			}

			const docs = resp.docs || [];
			const total = resp.numFound || 0;
			setURLFromState(this.props, this.state);
			setLocalStorage(lsPre, subFilterKey, JSON.stringify(this.state.subFilters));

			this.setState({
				tableData: docs,
				totalCount: total,
				start,
				page: resetPage ? 0 : this.state.page,
				fetchInProgress: false,
			});

			if (docs.length > 0) {
				this._fetchEnrichment(docs, requestId);
			} else {
				this.setState({ casesByProperty: {}, liensByProperty: {} });
			}
		} catch (e) {
			console.error('[ForeclosureSearch] property query failed', e);
			if (requestId === this._requestId) {
				this.setState({ fetchInProgress: false, tableData: [], totalCount: 0 });
			}
		}
	};

	_buildCaseQuery() {
		// Build q/fq for a query against nyscef-cases-foreclosures.
		// Cases have property fields (borough, ownership_type, alternate_addresses) embedded,
		// so most filters apply directly. additional_fq joins targeting cases are stripped
		// to their inner query; ACRIS lien joins are rewritten with to=property.
		const fq = ['deleted:false'];
		let q = '*:*';
		const { subFilters, searchText } = this.state;

		if (subFilters && Object.keys(subFilters).length > 0) {
			Object.keys(subFilters).forEach(key => {
				const val = subFilters[key];
				if (!val || val === '') return;
				const filterDef = _foreclosureSearchFilters.find(f => f.search_key === key);
				if (!filterDef || !filterDef.multi_filter) return;
				const selected = Array.isArray(val) ? val : [val];
				const matched = filterDef.values.filter(v => selected.indexOf(v.filter) > -1);

				const qVals = matched.filter(v => v.search_value).map(v => v.search_value);
				if (qVals.length > 0) {
					fq.push(`${key}:(${qVals.join(' OR ')})`);
				}

				matched.filter(v => v.additional_fq).forEach(v => {
					const aq = v.additional_fq;
					if (aq.indexOf('nyscef-cases-foreclosures') >= 0) {
						const inner = aq.replace(/^\{!join[^}]+\}/, '');
						fq.push(inner);
					} else if (aq.indexOf('we3-acris-real-property') >= 0) {
						const inner = aq.replace(/^\{!join[^}]+\}/, '');
						fq.push(`{!join method=crossCollection from=property fromIndex=we3-acris-real-property to=property}${inner}`);
					}
				});
			});
		}

		if (searchText && searchText.trim()) {
			const tokens = searchText.trim().split(/\s+/);
			const tokenQ = tokens
				.map(t => `(alternate_addresses:(*${t.toLowerCase()}*))`)
				.join(' AND ');
			q += ` AND (${tokenQ})`;
		}

		const { searchTags } = this.props;
		if (Array.isArray(searchTags) && searchTags.length > 0) {
			let tagQ = _tranformTagsQuery(searchTags, 'building');
			if (tagQ) {
				tagQ = tagQ.replace(/alternate_keys:/g, 'property:');
				q += ` AND (${tagQ})`;
			}
		}

		return { q, fq };
	}

	_getPropertiesCasePrimary = async (resetPage, requestId) => {
		const { q, fq } = this._buildCaseQuery();
		const order = this.state.order || 'desc';
		const start = resetPage ? 0 : this.state.start;
		const sortField = CASE_SORT_FIELDS[this.state.orderBy] || 'filing_date';

		// Collapse on property to get one representative case per property;
		// pick the most-recent (or oldest) row by the active sort field.
		// Solr's collapse min/max only supports numeric fields, so use the sort variant
		// which works on dates.
		const collapse = `{!collapse field=property sort='${sortField} ${order}'}`;
		const caseFq = [...fq, collapse];
		const sort = `${sortField} ${order}`;

		const caseQuery = {
			url: buildingUrl + 'nyscef-cases-foreclosures?cache=30',
			method: 'post',
			query: {
				q,
				fq: caseFq,
				sort,
				fl: 'id,property,index_number,caption,creditor,debtor,case_type,case_stage,case_stages,filing_date,lis_pendens_date,original_amount,court,case_url,case_documents,document_count,last_checked_at,has_auction,auction_postponed,auction_postponement_count,auction_sale_date,auction_sale_datetime,auction_location,auction_referee,auction_upset_price,auction_status,auction_history_dates,sold_date,sold_buyer,sold_price',
				rows: this.state.rowsPerPage,
				start,
				wt: 'json',
			},
		};

		try {
			const resp = await _axiosCall(caseQuery);
			if (requestId !== this._requestId) return;
			if (!resp || !resp.docs) {
				this.setState({ fetchInProgress: false, tableData: [], totalCount: 0 });
				return;
			}

			const cases = resp.docs || [];
			const total = resp.numFound || 0;
			const propertyKeys = cases.map(c => c.property).filter(Boolean);

			setURLFromState(this.props, this.state);
			setLocalStorage(lsPre, subFilterKey, JSON.stringify(this.state.subFilters));

			if (propertyKeys.length === 0) {
				this.setState({
					tableData: [],
					totalCount: total,
					start,
					page: resetPage ? 0 : this.state.page,
					fetchInProgress: false,
					casesByProperty: {},
					liensByProperty: {},
				});
				return;
			}

			const keyList = propertyKeys.map(k => `"${k}"`).join(' OR ');
			const propResp = await _axiosCall({
				url: buildingUrl + 'we3-properties?cache=30',
				method: 'post',
				query: {
					q: `key:(${keyList})`,
					fq: ['deleted:false'],
					fl: propertyFL,
					rows: propertyKeys.length,
					wt: 'json',
				},
			}).catch(() => null);
			if (requestId !== this._requestId) return;

			const propByKey = {};
			((propResp && propResp.docs) || []).forEach(p => { propByKey[p.key] = p; });
			const caseByProp = {};
			cases.forEach(c => { if (c.property) caseByProp[c.property] = c; });

			const orderedProps = propertyKeys
				.map(k => propByKey[k])
				.filter(Boolean)
				.map(p => ({ ...p, _primary_case: caseByProp[p.key] }));

			const casesByProperty = {};
			cases.forEach(c => {
				if (!c.property) return;
				if (!casesByProperty[c.property]) casesByProperty[c.property] = [];
				casesByProperty[c.property].push(c);
			});

			this.setState({
				tableData: orderedProps,
				totalCount: total,
				start,
				page: resetPage ? 0 : this.state.page,
				fetchInProgress: false,
				casesByProperty,
			});

			// Enrichment query to populate full case list + ACRIS liens for visible rows.
			this._fetchEnrichment(orderedProps, requestId);
		} catch (e) {
			console.error('[ForeclosureSearch] case-primary query failed', e);
			if (requestId === this._requestId) {
				this.setState({ fetchInProgress: false, tableData: [], totalCount: 0 });
			}
		}
	};

	_fetchEnrichment = async (docs, requestId) => {
		const propertyKeys = docs.map(d => d.key).filter(Boolean);
		if (propertyKeys.length === 0) return;
		const keyList = propertyKeys.map(k => `"${k}"`).join(' OR ');

		const caseQuery = {
			url: buildingUrl + 'nyscef-cases-foreclosures?cache=30',
			method: 'post',
			query: {
				q: `property:(${keyList})`,
				fq: ['deleted:false'],
				fl: 'id,key,property,index_number,caption,creditor,debtor,case_type,case_stage,case_stages,filing_date,lis_pendens_date,original_amount,court,case_url,case_documents,document_count,last_checked_at,has_auction,auction_postponed,auction_postponement_count,auction_sale_date,auction_sale_datetime,auction_location,auction_referee,auction_upset_price,auction_status,auction_history_dates,sold_date,sold_buyer,sold_price',
				rows: 500,
				sort: 'filing_date desc',
				wt: 'json',
			},
		};

		const lienQuery = {
			url: buildingUrl + 'we3-acris-real-property?cache=30',
			method: 'post',
			query: {
				q: `property:(${keyList}) AND doc_type:(${ACRIS_DISTRESS_DOC_TYPES.join(' ')}) AND recorded_datetime:[NOW-5YEARS TO NOW]`,
				fq: [],
				fl: 'id,property,doc_type,recorded_datetime,document_amt,doc_date,party1,party2',
				rows: 500,
				sort: 'recorded_datetime desc',
				wt: 'json',
			},
		};

		try {
			const [caseResp, lienResp] = await Promise.all([
				_axiosCall(caseQuery),
				_axiosCall(lienQuery).catch(() => null),
			]);
			if (requestId !== this._requestId) return;

			const casesByProperty = {};
			const caseDocs = (caseResp && caseResp.docs) || [];
			caseDocs.forEach(c => {
				if (!c.property) return;
				if (!casesByProperty[c.property]) casesByProperty[c.property] = [];
				casesByProperty[c.property].push(c);
			});

			const liensByProperty = {};
			const lienDocs = (lienResp && lienResp.docs) || [];
			lienDocs.forEach(l => {
				if (!l.property) return;
				if (!liensByProperty[l.property]) liensByProperty[l.property] = [];
				liensByProperty[l.property].push(l);
			});

			this.setState({ casesByProperty, liensByProperty });
		} catch (e) {
			console.error('[ForeclosureSearch] enrichment query failed', e);
		}
	};

	_setSubFilter = (key, val) => {
		const next = { ...this.state.subFilters };
		if (val === null || val === undefined || (Array.isArray(val) && val.length === 0)) {
			delete next[key];
		} else {
			next[key] = val;
		}
		this.setState({ subFilters: next, page: 0, start: 0 });
	};

	_setSubFilters = (filters) => {
		this.setState({ subFilters: filters, page: 0, start: 0 });
	};

	_resetSubFilter = () => {
		this.setState({ subFilters: {}, searchText: '', page: 0, start: 0 });
	};

	_handleSearch = (text) => {
		this.setState({ searchText: text || '', page: 0, start: 0 });
	};

	_handleRequestSort = (event, property) => {
		const orderBy = property;
		let order = 'desc';
		if (this.state.orderBy === property && this.state.order === 'desc') order = 'asc';
		this.setState({ order, orderBy });
	};

	_handleChangePage = (event, page) => {
		this.setState({ page, start: page * this.state.rowsPerPage });
	};

	_handleChangeRowsPerPage = (event) => {
		this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0, start: 0 });
	};

	_selectUnit = (property) => {
		const { selectedUnits } = this.state;
		const next = Object.assign({}, selectedUnits);
		if (next[property.key]) {
			delete next[property.key];
		} else {
			next[property.key] = property;
		}
		this.setState({ selectedUnits: next });
	};

	_clearSelectedUnits = () => {
		this.setState({ selectedUnits: {} });
	};

	_openModal = (property) => {
		const cases = this.state.casesByProperty[property.key] || [];
		const liens = this.state.liensByProperty[property.key] || [];
		const primary = pickPrimaryCase(cases);
		this.setState({
			modalOpen: true,
			modalCase: primary,
			modalProperty: property,
			modalLiens: liens,
		});
	};

	_closeModal = () => {
		this.setState({ modalOpen: false, modalCase: null, modalProperty: null, modalLiens: [] });
	};

	handleOpenModal = (key) => {
		this.setState({ openModalKey: key });
	};

	handleCloseModal = () => {
		this.setState({ openModalKey: null });
	};

	// Owner column with skip-trace treatment (matches Owner Outreach)
	_renderOwnerCell = (p, idx) => {
		const { user } = this.props;

		if (!p.owner || p.owner === 'unavailableowner') {
			return <span className="light-text small-text">--</span>;
		}

		let owners = _extractIndividualNames(p.owner);
		if (owners.length === 0) {
			owners = [String(p.owner)];
		}

		return owners.map((name, oidx) => {
			const person = _getFirstAndLastFromFullName(name);
			const displayName = person.display
				? person.display
				: [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(' ');
			const modalKey = (p.key || idx) + '-owner-' + oidx;
			return (
				<SkipTraceUnifiedModal
					user={user}
					key={'stm-' + oidx}
					first_name={'' + person.first_name}
					last_name={'' + person.last_name}
					address_1={'' + p.display_full_street_address}
					address_2={'' + p.address_2_normalized}
					city={'' + p.city}
					county={'' + p.county}
					state={'' + p.state}
					zip={'' + p.zip}
					triggerDisplay={_capitalizeText(displayName)}
					borough_code={p.borough_code}
					block={p.block}
					lot={p.lot}
					property={p}
					isOpen={this.state.openModalKey === modalKey}
					onOpen={() => this.handleOpenModal(modalKey)}
					onClose={this.handleCloseModal}
				/>
			);
		});
	};

	_handleAddressClick = (e, property) => {
		e.stopPropagation();
		const buildingUrl = _getBuildingURL(property, 'building_slugs');
		const hasUnit = !_isHouse(property) && property.address_2_normalized;
		const url = hasUnit
			? buildingUrl + '/' + _nameToUrl(property.address_2_normalized)
			: buildingUrl;
		this.props.history.push(url);
	};

	_renderDistressBadges(property, primaryCase, cases, liens) {
		const badges = [];
		if (primaryCase) {
			if (primaryCase.case_stage === 'auction_scheduled' || primaryCase.has_auction) {
				if (primaryCase.auction_status === 'sold') {
					badges.push({ label: 'Sold at Auction', palette: PALETTES.green });
				} else {
					badges.push({ label: 'Auction', palette: PALETTES.orange });
				}
			} else {
				badges.push({ label: 'Lis Pendens', palette: PALETTES.red });
			}
		}
		if (liens && liens.length > 0) {
			badges.push({ label: `${liens.length} Lien${liens.length > 1 ? 's' : ''}`, palette: PALETTES.grey });
		}
		if (cases && cases.length > 1) {
			badges.push({ label: `+${cases.length - 1} case${cases.length > 2 ? 's' : ''}`, palette: PALETTES.grey });
		}
		return (
			<div className="fs-badge-stack">
				{badges.map((b, i) => (
					<span key={i} style={badgeStyle(b.palette)}>
						{b.label}
					</span>
				))}
			</div>
		);
	}

	_renderFrozenCell(property) {
		const { selectedUnits } = this.state;
		const display = _capitalizeText(
			_replaceAbbreviations(property.display_full_street_address || property.full_street_address || '')
		);
		const unit = property.address_2_normalized
			? ', ' + property.address_2_normalized.toUpperCase().replace('APT', 'Apt')
			: '';
		const hood = Array.isArray(property.neighborhood) && property.neighborhood[0]
			? _capitalizeText(property.neighborhood[0]) + ', '
			: '';
		const borough = property.borough ? _capitalizeText(property.borough) : '';
		const photoUrl = _getBuildingPhotos(property, '')[0];
		const checked = selectedUnits[property.key] !== undefined;
		return (
			<div className="ps-table__frozen-content">
				<div className="ps-table__checkbox">
					<Checkbox
						checked={checked}
						onClick={(e) => { e.preventDefault(); e.stopPropagation(); this._selectUnit(property); }}
						className={`material-table__checkbox ${checked ? 'material-table__checkbox--checked' : ''}`}
					/>
				</div>
				<div className="ps-table__photo" style={{ width: 40, height: 40, minWidth: 40, borderRadius: 4, overflow: 'hidden', marginRight: 8, backgroundColor: '#f0f0f0' }}>
					{photoUrl && (
						<img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
					)}
				</div>
				<div className="ps-table__address">
					<span className="building_name">
						<a
							href="#"
							onClick={(e) => { e.preventDefault(); this._handleAddressClick(e, property); }}
							style={{ color: 'inherit', textDecoration: 'none' }}
						>
							{display}{unit}
						</a>
						<BuildingIcons building={property} />
					</span>
					<br />
					<span className="light-text small-text">
						{hood}{borough}{property.zip ? ' ' + property.zip : ''}
					</span>
				</div>
			</div>
		);
	}

	render() {
		const {
			tableData,
			fetchInProgress,
			totalCount,
			page,
			rowsPerPage,
			order,
			orderBy,
			subFilters,
			searchText,
			casesByProperty,
			liensByProperty,
			selectedUnits,
			modalOpen,
			modalCase,
			modalProperty,
			modalLiens,
		} = this.state;

		return (
			<Col md={12}>
				<Card>
					<CardBody>
						<UnitsSearchAppBar
							handleSearch={this._handleSearch}
							numSelected={0}
							handleDeleteSelected={() => {}}
							onRequestSort={this._handleRequestSort}
							subFilters={subFilters}
							setSubFilter={this._setSubFilter}
							setSubFilters={this._setSubFilters}
							resetSubFilter={this._resetSubFilter}
							_filters={_foreclosureSearchFilters}
							searchText={searchText}
							buildingAttributes={[]}
							placeHolder={'Building, Neighborhood or Borough'}
							numToShow={5}
							resetActive={Object.keys(subFilters).length > 0 || !!searchText}
							showExport={false}
							hideBuildingStage={true}
						/>

						<ToggleChips
							chipData={FORECLOSURE_TYPE_CHIPS}
							subFilters={subFilters}
							setSubFilters={this._setSubFilters}
						/>

						{fetchInProgress && <LinearLoading />}

						<div className="material-table__header-info">
							<span>
								<span className="bold-text">{totalCount > 0 ? _formatNumber(totalCount) : '--'}</span>
								{' '}
								{totalCount === 1 ? 'Property' : 'Properties'}
							</span>
						</div>

						<div className="material-table__wrap property-search-table fs-table-wrap">
							<Table className="material-table">
								<TableHead>
									<TableRow className="material-table__row">
										{HEADERS.map(h => {
											const isFrozen = h.id === 'address';
											return (
												<TableCell
													key={h.id}
													padding={isFrozen ? 'none' : 'default'}
													className={
														'material-table__cell material-table__cell--sort material-table__cell-right' +
														(isFrozen ? ' ps-table__cell--frozen' : '')
													}
													sortDirection={orderBy === h.id ? order : false}
												>
													{h.sort ? (
														<Tooltip title="Sort" enterDelay={300}>
															<TableSortLabel
																active={orderBy === h.id}
																direction={order}
																onClick={(e) => this._handleRequestSort(e, h.id)}
																className="material-table__sort-label"
															>
																{h.label}
															</TableSortLabel>
														</Tooltip>
													) : (
														h.label
													)}
												</TableCell>
											);
										})}
									</TableRow>
								</TableHead>
								<TableBody>
									{Array.isArray(tableData) && tableData.length === 0 && !fetchInProgress && (
										<TableRow className="material-table__row">
											<TableCell colSpan={HEADERS.length} className="material-table__cell fs-empty">
												No properties found matching the current filters.
											</TableCell>
										</TableRow>
									)}
									{Array.isArray(tableData) && tableData.map((p, idx) => {
										const cases = casesByProperty[p.key] || [];
										const liens = liensByProperty[p.key] || [];
										const primary = p._primary_case || pickPrimaryCase(cases);
										return (
											<TableRow
												key={p.key || idx}
												hover
												className="material-table__row fs-row"
												onClick={() => this._openModal(p)}
											>
												<TableCell padding="none" className="material-table__cell ps-table__cell--frozen">
													{this._renderFrozenCell(p)}
												</TableCell>
												<TableCell className="material-table__cell">
													{this._renderDistressBadges(p, primary, cases, liens)}
												</TableCell>
												<TableCell
													className="material-table__cell fs-cell-owner"
													onClick={(e) => { e.stopPropagation(); }}
												>
													{this._renderOwnerCell(p, idx)}
												</TableCell>
												<TableCell className="material-table__cell">
													<span className="light-text small-text">
														{p.ownership_type ? _capitalizeText(p.ownership_type) : '--'}
													</span>
												</TableCell>
												<TableCell className="material-table__cell">
													<span className="light-text small-text">
														{primary ? _formatDate(primary.lis_pendens_date) : '--'}
													</span>
												</TableCell>
												<TableCell className="material-table__cell">
													<span className="light-text small-text">
														{primary ? _formatDate(primary.filing_date) : '--'}
													</span>
												</TableCell>
												<TableCell className="material-table__cell">
													<span className="light-text small-text">
														{primary && primary.original_amount
															? '$' + _formatNumber(primary.original_amount)
															: '--'}
													</span>
												</TableCell>
												<TableCell className="material-table__cell">
													{primary && primary.case_stage ? (
														<span style={badgeStyle(CASE_STAGE_PALETTE[primary.case_stage] || PALETTES.grey)}>
															{CASE_STAGE_LABELS[primary.case_stage] || primary.case_stage}
														</span>
													) : (
														<span className="light-text small-text">--</span>
													)}
												</TableCell>
												<TableCell className="material-table__cell">
													{primary && primary.auction_sale_date ? (
														<span className="light-text small-text">
															<span className="fs-auction-date">
																{_formatDate(primary.auction_sale_date)}
															</span>
															{primary.auction_status && (
																<>
																	<br />
																	<span
																		className="fs-auction-status"
																		style={{
																			color: AUCTION_STATUS_COLORS[primary.auction_status] || '#A3ADC2',
																		}}
																	>
																		{_capitalizeText(primary.auction_status)}
																	</span>
																</>
															)}
														</span>
													) : (
														<span className="light-text small-text">--</span>
													)}
												</TableCell>
												<TableCell className="material-table__cell">
													<span className="light-text small-text">
														{primary && primary.auction_upset_price
															? '$' + _formatNumber(primary.auction_upset_price)
															: '--'}
													</span>
												</TableCell>
												<TableCell className="material-table__cell">
													{p.last_sold_price && p.last_sold_price > 0 ? (
														<span className="light-text small-text">
															${_formatNumber(p.last_sold_price)}
															<br />
															<span className="fs-sub">{_formatDate(p.last_sold_date)}</span>
														</span>
													) : (
														<span className="light-text small-text">--</span>
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</div>

						<TablePagination
							component="div"
							className="material-table__pagination"
							count={totalCount}
							rowsPerPage={rowsPerPage}
							page={page}
							backIconButtonProps={{ 'aria-label': 'Previous Page' }}
							nextIconButtonProps={{ 'aria-label': 'Next Page' }}
							onChangePage={this._handleChangePage}
							onChangeRowsPerPage={this._handleChangeRowsPerPage}
							rowsPerPageOptions={[25, 50, 100]}
							dir="ltr"
							SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
						/>

						<ForeclosureDetailModal
							isOpen={modalOpen}
							onClose={this._closeModal}
							foreclosureCase={modalCase}
							property={modalProperty}
							liens={modalLiens}
							allCases={modalProperty ? (this.state.casesByProperty[modalProperty.key] || []) : []}
							user={this.props.user}
						/>

						<AddCollectionButton
							selectedUnits={selectedUnits}
							groupType={'buildings'}
							clearSelectedUnits={this._clearSelectedUnits}
							{...this.props}
							addMultiple
						/>
					</CardBody>
				</Card>
			</Col>
		);
	}
}

export default withRouter(connect(state => ({
	user: state.user,
	searchTags: Array.isArray(state.search && state.search.searchTags) ? state.search.searchTags.slice() : [],
}))(ForeclosureSearchTable));
